/**
 * NextActions — "¿qué necesita tu atención ahora?" consolidado.
 *
 * El industrialist abre el dashboard y ve en una sola franja las cosas
 * que requieren su atención en orden cronológico — sin tener que navegar
 * a /industry, /planetary, /skills, etc para juntar mentalmente "qué hago
 * primero".
 *
 * Fuentes (4, todas con api methods ya cacheadas en TanStack Query):
 *   1. Industry jobs — active/ready, sorted by end_date ASC.
 *   2. Calendar events — upcoming (top 5 from /api/calendar/upcoming).
 *   3. Skill queue — siguiente skill a entrenar, finish_date ASC.
 *   4. PI extractors — expiring en las próximas 168h (top 5 from
 *      /api/planetary/expiring). 1 entry por planet_id (no spam).
 *
 * PI sí entra: el helper `loadColonyDetail` está cacheado en KV 30min,
 * así que el fan-out cold (~30-50 subrequests) es one-time per mount.
 *
 * Cada action:
 *   - kind badge ("industry" / "event" / "skill" / "PI")
 *   - title + sub (qué pasa cuando llega el momento)
 *   - countdown live-ticking (HH:MM:SS / "Dd Hh" / "listo")
 *   - link a la page relevante para action
 *
 * Top 5 actions, sorted by `when` ASC. Si no hay nada → "todo tranquilo".
 *
 * Data flow:
 *   - Re-usa TanStack Query cache de otras widgets del Dashboard
 *     (ActiveJobsEta / UpcomingEvents ya cargan industry-valuation y
 *     calendar-upcoming). Si el cache está warm, este widget es casi
 *     gratis. Si no, dispara las mismas queries — TanStack deduplica.
 *   - Live ticker (1s) para el countdown — mismo patrón que ActiveJobsEta.
 *   - No rompe el dashboard si alguna fuente falla: el row se omite.
 *
 * Caveat de subrequest budget: si las 4 fuentes disparan cold, son 3
 * ESI calls baratas (skill queue + industry jobs + calendar) + 1 fan-out
 * de N colony details (~30-50 subrequests en cold). Para KV warm, son 0.
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, formatISKShort } from '../api'

interface Action {
  id: string
  kind: 'industry' | 'calendar' | 'skill' | 'pi'
  title: string
  sub: string
  when: Date
  /** Optional emphasis ("entregar", "asistir", "queuear"). */
  hint: string
  /** Right-aligned meta (e.g. margin total, owner name). */
  meta?: string
  linkTo: string
}

const TOP_N = 5
const SKILL_NAME_FALLBACK = 'Skill'

export function NextActions() {
  // Las 3 queries — TanStack las dedupa si ya están en cache (de
  // ActiveJobsEta / UpcomingEvents / el dashboard mount).
  const { data: industry } = useQuery({
    queryKey: ['industry-valuation', 'active-jobs-widget'],
    queryFn: () => api.industryValuation(false, false, 'fuzzwork'),
    staleTime: 60_000,
  })
  const { data: calendar } = useQuery({
    queryKey: ['calendar-upcoming', 5],
    queryFn: () => api.calendarUpcoming(5),
    staleTime: 5 * 60_000,
  })
  const { data: skill } = useQuery({
    queryKey: ['skill-queue'],
    queryFn: () => api.skillQueue(),
    staleTime: 60_000,
  })
  const { data: planetary } = useQuery({
    queryKey: ['planetary-expiring', 168, 5],
    queryFn: () => api.planetaryExpiring(168, 5),
    // 30min stale — match el cache TTL del colony detail. El user no
    // necesita freshness sub-minute acá (los extractors tardan horas en
    // pasar; si uno vence ya el widget lo muestra como "ahora").
    staleTime: 30 * 60_000,
  })

  // Live ticker.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  // Componer actions. Errores por fuente = ignorar esa fuente, no romper
  // el resto (mismo patrón que EmpireAggregate).
  const actions = useMemo<Action[]>(() => {
    const list: Action[] = []

    if (industry?.jobs) {
      for (const j of industry.jobs) {
        if (j.status !== 'active' && j.status !== 'ready') continue
        const end = new Date(j.end_date).getTime()
        if (Number.isNaN(end)) continue
        list.push({
          id: `industry-${j.job_id}`,
          kind: 'industry',
          title: j.blueprint_name,
          sub:
            j.status === 'ready'
              ? `Listo para entregar · ${j.runs} ${j.runs === 1 ? 'run' : 'runs'}`
              : `${j.runs} ${j.runs === 1 ? 'run' : 'runs'} en curso`,
          when: new Date(j.end_date),
          hint: j.status === 'ready' ? 'entregar' : 'entregar al terminar',
          meta:
            j.margin_total !== null
              ? `${j.margin_total >= 0 ? '+' : '−'}${formatISKShort(
                  Math.abs(j.margin_total),
                )} ISK`
              : 'research',
          linkTo: '/industry',
        })
      }
    }

    if (calendar?.events) {
      for (const e of calendar.events) {
        const when = new Date(e.event_date).getTime()
        if (Number.isNaN(when)) continue
        list.push({
          id: `calendar-${e.event_id}`,
          kind: 'calendar',
          title: e.title,
          sub: `por ${e.owner_name} · ${e.duration} min`,
          when: new Date(e.event_date),
          hint:
            e.response === 'accepted'
              ? 'asistir'
              : e.response === 'declined'
              ? 'rechazado'
              : 'confirmar',
          meta: e.importance ? 'importante' : undefined,
          linkTo: '/calendar',
        })
      }
    }

    if (skill?.queue) {
      // Sólo el primer skill de la queue (el que termina primero) — los
      // demás son lejanos y no son "next action".
      const next = [...skill.queue]
        .filter((s) => s.finish_date)
        .sort(
          (a, b) =>
            new Date(a.finish_date!).getTime() -
            new Date(b.finish_date!).getTime(),
        )[0]
      if (next && next.finish_date) {
        list.push({
          id: `skill-${next.skill_id}`,
          kind: 'skill',
          title: next.name || SKILL_NAME_FALLBACK,
          sub: `Termina L${next.finished_level}`,
          when: new Date(next.finish_date),
          hint: 'queuear próximo',
          linkTo: '/skills',
        })
      }
    }

    if (planetary?.extractors) {
      for (const ext of planetary.extractors) {
        const when = new Date(ext.expiry_time).getTime()
        if (Number.isNaN(when)) continue
        // Mostrar 1 extractor por planet_id para no spammear el consolidado
        // cuando un char tiene 4 extractors que vencen en la misma hora.
        // El usuario navega a /planetary para ver todos.
        if (list.some((a) => a.kind === 'pi' && a.id === `pi-${ext.planet_id}`)) {
          continue
        }
        list.push({
          id: `pi-${ext.planet_id}-${ext.product_type_id}`,
          kind: 'pi',
          title: ext.product_name ?? `Product #${ext.product_type_id}`,
          sub: `${ext.planet_type_name} · ${ext.solar_system_name ?? 'unknown system'} · ${ext.quantity_per_cycle}/cycle · ${ext.cycle_time_hours}h`,
          when: new Date(ext.expiry_time),
          hint: 'relanzar antes de que venza',
          meta:
            ext.isk_per_hour !== null
              ? `${formatISKShort(ext.isk_per_hour)} ISK/h`
              : undefined,
          linkTo: '/planetary',
        })
      }
    }

    // Sort por `when` ASC + cap a TOP_N. Filtrar events que ya pasaron
    // (puede pasar si el endpoint devuelve algo viejo + KV cache).
    return list
      .filter((a) => a.when.getTime() > now - 60_000)
      .sort((a, b) => a.when.getTime() - b.when.getTime())
      .slice(0, TOP_N)
  }, [industry, calendar, skill, now])

  if (actions.length === 0) {
    return (
      <div className="bg-eve-panel border border-eve-border rounded-md p-4 text-sm italic text-eve-muted">
        Todo tranquilo por ahora — sin jobs por terminar, eventos o skills
        próximos.
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-br from-eve-panel to-eve-bg border border-eve-accent/30 rounded-md p-4 shadow-gold">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <div className="text-eve-muted text-[10px] uppercase tracking-widest font-medium">
            Next actions
          </div>
          <div className="font-display text-lg font-semibold text-eve-text mt-0.5">
            {actions.length}{' '}
            {actions.length === 1 ? 'cosa por hacer' : 'cosas por hacer'}
          </div>
        </div>
        <div className="text-[10px] text-eve-muted-2 font-mono italic">
          live · ticking cada 1s
        </div>
      </div>
      <ul className="mt-3 space-y-2">
        {actions.map((a) => (
          <ActionRow key={a.id} action={a} now={now} />
        ))}
      </ul>
    </div>
  )
}

function ActionRow({ action, now }: { action: Action; now: number }) {
  const remainingMs = action.when.getTime() - now
  const past = remainingMs <= 0
  const tone = toneForKind(action.kind)
  return (
    <li>
      <Link
        to={action.linkTo}
        className={`flex items-center gap-3 px-3 py-2 rounded border transition-colors hover:bg-eve-bg/40 ${
          past ? 'border-eve-success/40 bg-eve-success/5' : 'border-eve-border/60'
        }`}
      >
        <span
          className={`text-[10px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 border shrink-0 ${tone}`}
        >
          {labelForKind(action.kind)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-eve-text truncate">
            {action.title}
          </div>
          <div className="text-[11px] text-eve-muted truncate">
            {action.sub}
            {action.hint && (
              <>
                <span className="text-eve-muted-2"> · </span>
                <span className="italic">{action.hint}</span>
              </>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          {past ? (
            <span className="text-[10px] uppercase tracking-wider font-semibold text-eve-success">
              ahora
            </span>
          ) : (
            <span className="font-mono text-sm font-semibold text-eve-accent tabular-nums">
              {formatRemaining(remainingMs)}
            </span>
          )}
          {action.meta && (
            <div className="text-[10px] text-eve-muted-2 mt-0.5 font-mono italic">
              {action.meta}
            </div>
          )}
        </div>
      </Link>
    </li>
  )
}

// --- helpers ---

function labelForKind(kind: Action['kind']): string {
  switch (kind) {
    case 'industry':
      return 'industry'
    case 'calendar':
      return 'event'
    case 'skill':
      return 'skill'
    case 'pi':
      return 'PI'
  }
}

function toneForKind(kind: Action['kind']): string {
  switch (kind) {
    case 'industry':
      return 'bg-eve-accent/10 text-eve-accent border-eve-accent/30'
    case 'calendar':
      return 'bg-eve-info/10 text-eve-info border-eve-info/30'
    case 'skill':
      return 'bg-eve-warn/10 text-eve-warn border-eve-warn/30'
    case 'pi':
      return 'bg-eve-success/10 text-eve-success border-eve-success/30'
  }
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return 'ahora'
  const totalSec = Math.floor(ms / 1000)
  const days = Math.floor(totalSec / 86_400)
  const hours = Math.floor((totalSec % 86_400) / 3_600)
  const minutes = Math.floor((totalSec % 3_600) / 60)
  const seconds = totalSec % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${pad(minutes)}m`
  if (minutes > 0) return `${minutes}m ${pad(seconds)}s`
  return `${seconds}s`
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}
